import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Building2, MapPin, Calendar, TrendingUp,
    CheckCircle, Clock, AlertCircle, Layers, ChevronRight,
    Plus, HardHat
} from 'lucide-react';
import { Button, CardStats, Card } from '@components/ui';
import { getConstructionById } from '@mocks/insaatlar';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import { getBlokIlerlemOzeti } from '@mocks/daireIlerleme';
import clsx from 'clsx';

export default function InsaatDetay() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [construction, setConstruction] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            const data = getConstructionById(id);
            if (data) {
                setConstruction(data);
                setBlocks(getBlocksByConstructionId(id));
            }
            setIsLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [id]);

    // Blok özet verileri
    const blokOzetleri = useMemo(() => {
        return blocks.map(blok => ({
            ...blok,
            ilerleme: getBlokIlerlemOzeti(blok.id),
        }));
    }, [blocks]);

    // Genel istatistikler
    const stats = useMemo(() => {
        const toplamDaire = blokOzetleri.reduce((a, b) => a + (b.ilerleme?.toplamDaire || 0), 0);
        const tamamlanan = blokOzetleri.reduce((a, b) => a + (b.ilerleme?.tamamlanan || 0), 0);
        const devamEden = blokOzetleri.reduce((a, b) => a + (b.ilerleme?.devamEden || 0), 0);
        const baslamamis = blokOzetleri.reduce((a, b) => a + (b.ilerleme?.baslamamis || 0), 0);
        const toplamIlerleme = toplamDaire > 0
            ? Math.round(blokOzetleri.reduce((a, b) => a + (b.ilerleme?.genelIlerleme || 0) * (b.ilerleme?.toplamDaire || 0), 0) / toplamDaire)
            : 0;
        return { toplamDaire, tamamlanan, devamEden, baslamamis, toplamIlerleme };
    }, [blokOzetleri]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const statusConfig = {
        planning: { label: 'Planlama', color: 'warning', icon: Clock },
        active: { label: 'Devam Ediyor', color: 'success', icon: TrendingUp },
        completed: { label: 'Tamamlandı', color: 'primary', icon: CheckCircle },
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-6 space-y-6 animate-pulse">
                <div className="h-8 w-48 bg-bg-tertiary rounded" />
                <div className="h-32 bg-bg-tertiary rounded-xl" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-bg-tertiary rounded-xl" />)}
                </div>
            </div>
        );
    }

    if (!construction) {
        return (
            <div className="p-4 md:p-6 text-center py-16">
                <Building2 size={64} className="mx-auto mb-4 text-text-muted opacity-40" />
                <h2 className="text-xl font-bold text-text-primary mb-2">İnşaat Bulunamadı</h2>
                <Button variant="primary" onClick={() => navigate('/insaat/projeler')}>Projelere Dön</Button>
            </div>
        );
    }

    const currentStatus = statusConfig[construction.status] || statusConfig.active;
    const StatusIcon = currentStatus.icon;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Breadcrumb + Back */}
            <div className="flex items-center gap-2 text-sm text-text-muted">
                <button onClick={() => navigate('/insaat/projeler')} className="hover:text-primary transition-colors flex items-center gap-1">
                    <ArrowLeft size={16} />
                    İnşaatlar
                </button>
                <ChevronRight size={14} />
                <span className="text-text-primary font-medium">{construction.name}</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                    <div className={clsx(
                        'w-14 h-14 rounded-xl flex items-center justify-center shrink-0',
                        'bg-gradient-to-br shadow-lg',
                        currentStatus.color === 'success' && 'from-success/20 to-success/5',
                        currentStatus.color === 'warning' && 'from-warning/20 to-warning/5',
                        currentStatus.color === 'primary' && 'from-primary/20 to-primary/5',
                    )}>
                        <Building2 size={28} className={clsx(
                            currentStatus.color === 'success' && 'text-success',
                            currentStatus.color === 'warning' && 'text-warning',
                            currentStatus.color === 'primary' && 'text-primary',
                        )} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary">{construction.name}</h1>
                        <p className="text-text-muted flex items-center gap-1.5 mt-1 text-sm">
                            <MapPin size={14} />
                            {construction.district}, {construction.city}
                        </p>
                    </div>
                </div>
                <div className={clsx(
                    'px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 shrink-0',
                    currentStatus.color === 'success' && 'bg-success/10 text-success',
                    currentStatus.color === 'warning' && 'bg-warning/10 text-warning',
                    currentStatus.color === 'primary' && 'bg-primary/10 text-primary',
                )}>
                    <StatusIcon size={16} />
                    {currentStatus.label}
                </div>
            </div>

            {/* Genel İlerleme */}
            <Card padding="lg">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-text-secondary">Genel İlerleme (Daire Bazlı)</span>
                    <span className="text-lg font-bold text-text-primary">%{stats.toplamIlerleme}</span>
                </div>
                <div className="h-3 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                        className={clsx(
                            'h-full rounded-full transition-all duration-700',
                            stats.toplamIlerleme >= 75 ? 'bg-success' : stats.toplamIlerleme >= 40 ? 'bg-primary' : 'bg-warning'
                        )}
                        style={{ width: `${stats.toplamIlerleme}%` }}
                    />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-text-muted">
                    <span>Başlangıç: {formatDate(construction.startDate)}</span>
                    <span>Tahmini Bitiş: {formatDate(construction.estimatedEndDate)}</span>
                </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats title="Toplam Daire" value={stats.toplamDaire} icon={Building2} color="primary" size="sm" />
                <CardStats title="Tamamlanan" value={stats.tamamlanan} icon={CheckCircle} color="success" size="sm" />
                <CardStats title="Devam Eden" value={stats.devamEden} icon={Clock} color="warning" size="sm" />
                <CardStats title="Başlanmadı" value={stats.baslamamis} icon={AlertCircle} color="tertiary" size="sm" />
            </div>

            {/* Bloklar */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                        <Layers size={20} className="text-primary" />
                        Bloklar ({blocks.length})
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {blokOzetleri.map(blok => (
                        <BlokKarti
                            key={blok.id}
                            blok={blok}
                            onClick={() => navigate(`/insaat/projeler/${id}/blok/${blok.id}`)}
                        />
                    ))}
                </div>

                {blocks.length === 0 && (
                    <div className="text-center py-12 bg-bg-secondary rounded-xl border border-border-light">
                        <Layers size={48} className="mx-auto mb-4 text-text-muted opacity-40" />
                        <p className="text-text-secondary">Bu inşaatta henüz blok tanımlanmamış.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Blok Kartı
function BlokKarti({ blok, onClick }) {
    const ilerleme = blok.ilerleme;
    const genelIlerleme = ilerleme?.genelIlerleme || 0;

    return (
        <div
            onClick={onClick}
            className={clsx(
                'group bg-bg-secondary rounded-xl border border-border-light',
                'hover:border-primary/30 hover:shadow-lg transition-all duration-200',
                'cursor-pointer overflow-hidden'
            )}
        >
            {/* İlerleme bar üst çizgi */}
            <div className="h-1 bg-bg-tertiary">
                <div
                    className={clsx(
                        'h-full transition-all duration-500',
                        genelIlerleme >= 75 ? 'bg-success' : genelIlerleme >= 40 ? 'bg-primary' : genelIlerleme > 0 ? 'bg-warning' : 'bg-bg-tertiary'
                    )}
                    style={{ width: `${genelIlerleme}%` }}
                />
            </div>

            {/* Header */}
            <div className="p-4 pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Layers size={20} className="text-primary" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
                                {blok.name}
                            </h3>
                            <p className="text-xs text-text-muted">
                                {blok.totalFloors} Kat • {blok.totalApartments} Daire
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-text-primary">%{genelIlerleme}</span>
                        <ChevronRight size={18} className="text-text-muted group-hover:text-primary transition-colors" />
                    </div>
                </div>
            </div>

            {/* Daire durumları */}
            {ilerleme && (
                <div className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-success/10 rounded-lg p-2">
                            <p className="text-lg font-bold text-success">{ilerleme.tamamlanan}</p>
                            <p className="text-2xs text-text-muted">Bitti</p>
                        </div>
                        <div className="bg-warning/10 rounded-lg p-2">
                            <p className="text-lg font-bold text-warning">{ilerleme.devamEden}</p>
                            <p className="text-2xs text-text-muted">Devam</p>
                        </div>
                        <div className="bg-bg-tertiary rounded-lg p-2">
                            <p className="text-lg font-bold text-text-muted">{ilerleme.baslamamis}</p>
                            <p className="text-2xs text-text-muted">Bekliyor</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
